import {Dispatch, SetStateAction, useEffect, useState} from "react";
import {
  ButtonGroup,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Switch,
  Tooltip,
} from "@mui/material";
import {Grid} from "@mui/system";
import pxToRem from "hooks/usePxToRem";
import {CustomButton} from "ui";
import {
  ButtonWithDropdown,
  CustomModal,
  MessageModelsList,
  ModalWrapperFooter,
} from "components";
import ReactQuill, {Quill} from "react-quill";
import "react-quill/dist/quill.snow.css";
import "components/TextEditorStyle/editor.style.scss";
import {PostFollowUpType} from "../../ProcessFollowUpTypes";
import {FollowUp} from "views/ProcessDetails/refactor/types/groupActivities.types";
import useFollowUpModal from "hooks/useFollowUpModal";
import {ProcessTypeShort} from "util/getModalityAndOperation";
import useAlertMessage from "hooks/useAlertMessage";

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  processNumber: string;
  idGroupActivity: number;
  handlePostNewFollowUp: (followUpData: PostFollowUpType) => Promise<void>;
  handleUpdateFollowUp: (
    followUpId: number,
    followUpData: Partial<PostFollowUpType>,
  ) => Promise<void>;
  onCreateAndSendFollow: () => void;
  modalEdit?: boolean;
  setModalEdit?: Dispatch<SetStateAction<boolean>>;
  currentFollowUp?: FollowUp;
  modalType: ProcessTypeShort;
};

const NewFollowUp = (props: Props) => {
  const {
    isOpen,
    onCloseModal,
    processNumber,
    handlePostNewFollowUp,
    idGroupActivity,
    modalEdit,
    setModalEdit,
    handleUpdateFollowUp,
    currentFollowUp,
    onCreateAndSendFollow,
    modalType = "IM",
  } = props;

  const [isPrivateFollowUp, setIsPrivateFollowUp] = useState(false);
  const [editorText, setEditorText] = useState("");
  const [showAlert] = useAlertMessage();

  const handleSubmitFollowUp = async () => {
    try {
      if (modalEdit) {
        await handleUpdateFollowUp(currentFollowUp?.id ?? 0, {
          content: editorText,
          isPrivate: isPrivateFollowUp,
        });
      } else {
        await handlePostNewFollowUp({
          idGroupActivity: idGroupActivity,
          content: editorText,
          isPrivate: isPrivateFollowUp,
        });
      }
    } catch (error: any) {
      console.error(error);
      showAlert(error?.response?.data?.message);
    }
    handleCloseModal();
  };

  const handleCreateAndSendFollowUp = () => {
    handleSubmitFollowUp();
    onCreateAndSendFollow();
  };

  const handleCloseModal = () => {
    if (setModalEdit) {
      setModalEdit(false);
    }
    setEditorText("");
    setIsPrivateFollowUp(false);
    onCloseModal();
  };

  const onChangeTextEditorContent = (text: string) => {
    setEditorText(text);
  };

  const handleAddEditDataToInputs = () => {
    if (modalEdit && currentFollowUp) {
      setEditorText(currentFollowUp?.content ?? "");
      setIsPrivateFollowUp(currentFollowUp?.isPrivate);
    }
  };

  useEffect(() => {
    handleAddEditDataToInputs();
  }, [modalEdit, currentFollowUp]);

  const editorSizeStyle = Quill.import("attributors/style/size");
  editorSizeStyle.whitelist = ["10px", "12px", "14px", "24px"];
  Quill.register(editorSizeStyle, true);

  const textEditorStyles = {
    height: pxToRem(250),
    width: "100%",
  };

  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"],

      [{list: "ordered"}, {list: "bullet"}],

      [{size: ["10px", "12px", "14px", "24px"]}],
      [{header: [1, 2, 3, 4, 5, 6, false]}],
      [{color: []}, {background: []}],
      ["link"],
      ["clean"],
    ],
  };

  const {
    messageModels,
    handleSearch,
    selectedListItem,
    handleKeyDownOnSearchInput,
    searchRef,
    handleKeyDownOnTextEditor,
    showSidebar,
    handleClickOnListItem,
    editorRef,
    itemRefs,
    handleOnKeyDownEditor,
  } = useFollowUpModal();

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={`${
        modalEdit ? "Editar atualização" : "Nova atualização"
      } - ${processNumber}`}
      titleIcon="notes"
      maxWidth="lg"
      disableEscapeKeyDown
    >
      <DialogContent sx={{minHeight: "fit-content"}}>
        <MessageModelsList
          items={messageModels}
          selectedItem={selectedListItem}
          searchRef={searchRef}
          handleKeyDownOnSearch={handleKeyDownOnSearchInput}
          handleSearch={handleSearch}
          showSidebar={showSidebar}
          handleClickOnListItem={handleClickOnListItem}
          itemRefs={itemRefs}
        >
          <Grid size={12}>
            <ReactQuill
              theme="snow"
              placeholder="Digite ou pressione ' / ' para usar modelos"
              value={editorText}
              onChange={(value, delta) => {
                onChangeTextEditorContent(value);
                handleKeyDownOnTextEditor(delta);
              }}
              modules={modules}
              style={textEditorStyles}
              onKeyDown={(e) => handleOnKeyDownEditor(e?.key)}
              ref={editorRef}
            />
          </Grid>
          <Grid
            mt={pxToRem(50)}
            size={12}
            display="flex"
            justifyContent="flex-end"
          />
        </MessageModelsList>
        <Grid size={{xs: 12}} display="flex" justifyContent="flex-start">
          <FormGroup>
            <FormControlLabel
              checked={isPrivateFollowUp}
              control={
                <Tooltip title="Ao marcar como privado, a atualização não será enviada nos follow ups enviados.">
                  <Switch
                    value={isPrivateFollowUp}
                    onChange={() => setIsPrivateFollowUp(!isPrivateFollowUp)}
                  />
                </Tooltip>
              }
              labelPlacement="start"
              label="Privado"
            />
          </FormGroup>
        </Grid>
      </DialogContent>
      <ModalWrapperFooter>
        <Grid
          container
          display="flex"
          justifyContent="flex-end"
          gap={pxToRem(8)}
          width={"100%"}
        >
          <CustomButton onClickFn={handleCloseModal} variant="outlined">
            Cancelar
          </CustomButton>

          {/* Quando formos extender essa feature, podemos usar o Strategy Pattern com Factories 
          pra implementar isso de uma forma mais modular e limpa */}
          {modalType === "IA" && (
            <ButtonGroup>
              <CustomButton
                onClickFn={handleSubmitFollowUp}
              >{`${modalEdit ? "Salvar" : "Criar"} followUp`}</CustomButton>
              <CustomButton
                onClickFn={handleCreateAndSendFollowUp}
              >{`${modalEdit ? "Salvar" : "Criar"} e enviar followUp`}</CustomButton>
            </ButtonGroup>
          )}
          {modalType !== "IA" && (
            <ButtonWithDropdown
              options={[
                {
                  actionFn: handleSubmitFollowUp,
                  name: `${modalEdit ? "Salvar" : "Criar"} followUp`,
                },
              ]}
              onClickMainButton={handleCreateAndSendFollowUp}
            >
              {`${modalEdit ? "Salvar" : "Criar"} e enviar followUp`}
            </ButtonWithDropdown>
          )}
        </Grid>
      </ModalWrapperFooter>
    </CustomModal>
  );
};

export default NewFollowUp;
